import { ORBI_GUARDRAIL_LOCK_TITLE } from '@patient-ui/shared/constants';
import { OrbiMessageType } from '@patient-ui/shared/enums';
import {
  OrbiHistoryConversation,
  OrbiMessage,
} from '@patient-ui/shared/models';

import { isOrbiSoftGuardrailType } from './orbi-guardrail.util';

/**
 * Pure view-model helpers for the conversation-history list (PCSA-13169).
 *
 * The backend's `GET /history` does NOT expose a message count, an end-of-list
 * marker, or a read-only/guardrail flag, so the client infers them — EXACTLY as
 * one-labcorp-mobile does (`orbi_history_item.dart`). These are kept as pure
 * functions (framework-free, unit-testable) and reused by the history NgRx
 * slice + the list UI.
 */

/**
 * Number of turns in a stored conversation. Mirrors mobile
 * `messagesCount => history.length` — the backend returns no count field.
 */
export function orbiHistoryMessageCount(
  conversation: OrbiHistoryConversation
): number {
  return conversation.history?.length ?? 0;
}

/**
 * Display title for a conversation row. Prefers the backend/user title, else
 * the first turn's first item text, else a placeholder — mirrors mobile
 * `displayTitle`.
 */
export function orbiHistoryDisplayTitle(
  conversation: OrbiHistoryConversation
): string {
  const title = conversation.metadata?.title?.trim();
  if (title) {
    return title;
  }
  const firstText = conversation.history?.[0]?.items?.[0]?.text?.trim();
  return firstText || 'New Conversation';
}

/**
 * True when the conversation is locked (read-only) because its last assistant
 * turn is a non-soft guardrail. Mirrors mobile `OrbiHistoryItem.isLocked`
 * (`orbi_history_item.dart:36-51`): walk back to the last `assistant` turn; if
 * that turn's agent is `guardrail` and its `returnType` is not a soft type, the
 * conversation is locked. The backend exposes no explicit flag — this is the
 * same inference mobile makes. Drives the "Read-only" badge (PCSA-13169); the
 * lock ENFORCEMENT on a resumed conversation is PCSA-13170.
 *
 * IMPORTANT — `returnType` is usually ABSENT here. `GET /history` strips the
 * per-turn `returnType` metadata (`ChatMessageHelper.SanitizeForClient` removes
 * its internal keys), so the guardrail TYPE that mobile branches on generally
 * does not reach either client on this endpoint. Treating a type-less guardrail
 * turn as locked would mark a merely OFF-TOPIC conversation read-only forever,
 * so when the type is missing we fall back to the service's own lock signal: the
 * forced conversation title (`ORBI_GUARDRAIL_LOCK_TITLE`), which the controller
 * writes for exactly the locking types and NOT for OffTopic / ForeignLanguage.
 * With neither signal present the conversation stays usable — failing open here
 * is safe because the SERVER also enforces the lock (ONL-4772: a locked session
 * re-emits the crisis guardrail instead of answering), so a stale "unlocked"
 * badge cannot actually get a blocked prompt answered.
 */
export function orbiHistoryIsReadOnly(
  conversation: OrbiHistoryConversation
): boolean {
  const turns = conversation.history ?? [];
  for (let i = turns.length - 1; i >= 0; i--) {
    const turn = turns[i];
    if ((turn.role?.label ?? '').toLowerCase() !== 'assistant') {
      continue;
    }
    if ((turn.metadata?.agent ?? '').toLowerCase() !== 'guardrail') {
      return false;
    }
    const returnType = turn.metadata?.returnType;
    if (returnType) {
      return !isOrbiSoftGuardrailType(returnType);
    }
    // Type stripped by the service — fall back to the forced lock title.
    return hasOrbiGuardrailLockTitle(conversation);
  }
  return false;
}

/**
 * True when the service has stamped the conversation with its forced
 * guardrail-lock title. Written by `AgentController` for every LOCKING guardrail
 * trip (and never for OffTopic / ForeignLanguage), overriding any user-edited
 * title — so it is a reliable lock marker even after `GET /history` strips the
 * per-turn guardrail type.
 */
function hasOrbiGuardrailLockTitle(
  conversation: OrbiHistoryConversation
): boolean {
  const title = conversation.metadata?.title?.trim().toLowerCase();
  return title === ORBI_GUARDRAIL_LOCK_TITLE.toLowerCase();
}

/**
 * Maps a stored conversation's turns into the active-thread `OrbiMessage[]` for
 * resuming it (PCSA-13169). A `user`-role turn becomes a User message; any other
 * role (assistant/llm) becomes an Llm message. Text is the concatenation of the
 * turn's item texts. Routing/markdown/cards are the thread's concern (13174);
 * here every resumed turn is a plain `text` route — enough to re-render the
 * conversation. The session id is swapped separately by the resume action.
 */
export function orbiHistoryToMessages(
  conversation: OrbiHistoryConversation
): OrbiMessage[] {
  return (conversation.history ?? []).map((turn) => {
    const isUser = (turn.role?.label ?? '').toLowerCase() === 'user';
    const text = (turn.items ?? [])
      .map((item) => item.text ?? '')
      .join('')
      .trim();
    return {
      text,
      type: isUser ? OrbiMessageType.User : OrbiMessageType.Llm,
      route: 'text',
      agent: turn.metadata?.agent,
      messageId: turn.metadata?.messageId,
      returnType: turn.metadata?.returnType,
      guardrailTriggered: false,
    } as OrbiMessage;
  });
}

/**
 * The calendar day a conversation is dated to — the shared step behind BOTH
 * places a conversation states its date, so the two can render different FORMATS
 * without ever disagreeing about which day it was.
 *
 * `month` is 0-based and `weekday` is 0=Sunday, matching the `Date` getters they
 * come from.
 */
interface OrbiConversationDay {
  year: number;
  month: number;
  day: number;
  weekday: number;
}

/**
 * Resolve a conversation's calendar day, or `null` when there is nothing to date.
 *
 * A stored `createdAt` is read in UTC, NOT local time. It is a UTC instant (e.g.
 * `2026-03-07T00:00:00Z`) and mobile formats the parsed UTC DateTime directly, so
 * a midnight-UTC stamp has to render as that calendar date everywhere; the local
 * getters would shift it back a day in every timezone behind UTC, which is what
 * made the history row's test flake 03/07 -> 03/06.
 *
 * `fallback` is for a conversation the SERVER has no record of yet — one started
 * in this browser session, which carries no timestamp at all (`OrbiMessage` has
 * no date field and the thread slice gets its `startedAt` only on resume). Pass
 * `new Date()` and the caller gets today. It is read LOCALLY, deliberately
 * differing from the branch above: there is no server instant to stay faithful
 * to, and the user's own calendar day is the only date that is true for them —
 * UTC would print TOMORROW's date all evening for anyone behind UTC. The cost is
 * that such a conversation can show today in the thread and, after the service
 * stamps it, tomorrow in the history list; a wrong date on the live conversation
 * is the more visible of the two errors.
 *
 * All four fields come from ONE frame of reference, which is why they travel
 * together: a UTC-derived date rendered with a locally-derived weekday could name
 * a weekday that is not the one that date fell on.
 */
function resolveOrbiConversationDay(
  createdAt?: string | null,
  fallback?: Date
): OrbiConversationDay | null {
  const stored = createdAt ? new Date(createdAt) : null;
  if (stored && !isNaN(stored.getTime())) {
    return {
      year: stored.getUTCFullYear(),
      month: stored.getUTCMonth(),
      day: stored.getUTCDate(),
      weekday: stored.getUTCDay(),
    };
  }
  if (!fallback || isNaN(fallback.getTime())) {
    return null;
  }
  return {
    year: fallback.getFullYear(),
    month: fallback.getMonth(),
    day: fallback.getDate(),
    weekday: fallback.getDay(),
  };
}

/**
 * A conversation's date as `MM/DD/YYYY` — the HISTORY ROW's format (PCSA-13169).
 *
 * The in-chat divider used to share this function, on the reasoning that one
 * formatter meant one conversation could not show two different dates. Design
 * review 08/21 specified a different format for the chat ("Today, Aug 21, 2026"),
 * so the two surfaces now format separately — see `formatOrbiChatDate`. What they
 * still share is `resolveOrbiConversationDay`, which is the part that actually
 * mattered: the DAY is resolved once, so the two can never name different days.
 *
 * With no `createdAt` and no `fallback` the result is `''` — the history row's
 * existing behavior for a conversation that arrives without a date, which renders
 * nothing rather than a placeholder.
 */
export function formatOrbiConversationDate(
  createdAt?: string | null,
  fallback?: Date
): string {
  const resolved = resolveOrbiConversationDay(createdAt, fallback);
  if (!resolved) {
    return '';
  }
  return [
    String(resolved.month + 1).padStart(2, '0'),
    String(resolved.day).padStart(2, '0'),
    resolved.year,
  ].join('/');
}

/**
 * A conversation's date as the IN-CHAT divider states it (PCSA-13312, design
 * review 08/21): "the format is always day, three-letter month, date, year",
 * where the current day says `Today` in the day's place —
 *
 *   today  -> "Today, Aug 21, 2026"
 *   past   -> "Friday, Aug 14, 2026"
 *
 * `today` is both the reference for that first word AND the fallback date for a
 * conversation the server has not stamped yet, because for the thread those are
 * the same question: the caller passes the current instant and gets "Today" for a
 * conversation started in this session. Omit it and the divider can still date a
 * STORED conversation, but never as "Today" — there is nothing to compare against,
 * and printing a weekday for the current day is the smaller error of the two.
 *
 * The "Today" test compares the resolved day against `today`'s LOCAL calendar day,
 * which is the only day the user is actually having. For a stored conversation
 * that day was resolved in UTC (see `resolveOrbiConversationDay`), so around
 * midnight a conversation from a few hours ago can print its weekday instead of
 * "Today" — the pre-existing UTC tradeoff, not a new one. It cannot go the other
 * way and say "Today" on a date that is not the user's today, which is the
 * version of this that would read as a bug.
 */
export function formatOrbiChatDate(
  createdAt?: string | null,
  today?: Date
): string {
  const resolved = resolveOrbiConversationDay(createdAt, today);
  if (!resolved) {
    return '';
  }
  const isToday =
    !!today &&
    !isNaN(today.getTime()) &&
    resolved.year === today.getFullYear() &&
    resolved.month === today.getMonth() &&
    resolved.day === today.getDate();
  const lead = isToday ? 'Today' : ORBI_CHAT_DATE_WEEKDAYS[resolved.weekday];
  return `${lead}, ${ORBI_CHAT_DATE_MONTHS[resolved.month]} ${resolved.day}, ${
    resolved.year
  }`;
}

/**
 * Full weekday names for the in-chat date (design review 08/21 — "Friday, Aug 14,
 * 2026", not "Fri").
 *
 * Kept local rather than shared with the appointment card's identical list
 * (`orbi-appointment.util.ts`): that list is one half of a format specified for
 * check parity with the mobile app, and the two surfaces have already been
 * corrected apart once (the card keeps `MMM.` with a period, this one does not).
 * A shared table would make the next divergence look like a mistake.
 */
const ORBI_CHAT_DATE_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Three-letter month abbreviations with NO trailing period — design review 08/21
 * writes the in-chat date as "Today, Aug 21, 2026" / "Friday, Aug 14, 2026".
 *
 * Deliberately not the appointment card's `ORBI_MONTHS_ABBREVIATED`, which carries
 * a period on all twelve because mobile's pattern is the literal `MMM.` and those
 * cards are specified against the app. This surface is specified against the
 * portal's own design, and it is a portal-only surface, so there is no app string
 * to match.
 */
const ORBI_CHAT_DATE_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
